import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import UsuarioSecundarioService from '../../modules/services/UsuarioSecundarioService';
import '../css/gestaoUsuarios.css';

function EditarUsuarioSecundario({ portalSession, onUsuarioAtualizado }) {
    const navigate = useNavigate();
    const { cdUsuario } = useParams();

    const souMaster = !!portalSession && (portalSession.cdUsuarioMaster === null || portalSession.cdUsuarioMaster === undefined);

    const clientesDisponiveis = useMemo(() => {
        if (!portalSession) {
            return [];
        }
        const vistos = new Set();
        return (portalSession.clientesPerfis || []).filter((item) => {
            if (vistos.has(item.cdCliente)) {
                return false;
            }
            vistos.add(item.cdCliente);
            return true;
        });
    }, [portalSession]);

    const [modulos, setModulos] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [dsLogin, setDsLogin] = useState('');
    const [novaSenha, setNovaSenha] = useState('');
    const [dsEmail, setDsEmail] = useState('');
    const [nmUsuario, setNmUsuario] = useState('');
    const [cdClientesSelecionados, setCdClientesSelecionados] = useState([]);
    // { [cdCliente]: [cdModulo, ...] } — cada CNPJ tem seu próprio conjunto de módulos (RN018).
    const [modulosPorCliente, setModulosPorCliente] = useState({});
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState('');
    const [sucesso, setSucesso] = useState('');
    const [filtroCliente, setFiltroCliente] = useState('');
    const [filtroModulo, setFiltroModulo] = useState('');

    useEffect(() => {
        if (!souMaster) {
            return;
        }

        const carregarDados = async () => {
            try {
                const [listaModulos, detalhe] = await Promise.all([
                    UsuarioSecundarioService.listarModulos(),
                    UsuarioSecundarioService.buscarDetalhe(portalSession.usuario.cdUsuario, cdUsuario),
                ]);

                setModulos(listaModulos || []);
                setDsLogin(detalhe.dsLogin);
                setNmUsuario(detalhe.nmUsuario || '');
                setDsEmail(detalhe.dsEmail || '');

                const clientes = detalhe.clientes || [];
                setCdClientesSelecionados(clientes.map((item) => item.cdCliente));
                const mapaModulos = {};
                clientes.forEach((item) => {
                    mapaModulos[item.cdCliente] = (item.modulos || []).map((modulo) => modulo.cdModulo);
                });
                setModulosPorCliente(mapaModulos);
            } catch (error) {
                setErro(error.message || 'Não foi possível carregar o usuário.');
            } finally {
                setCarregando(false);
            }
        };

        carregarDados();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [souMaster, cdUsuario]);

    const clientesSelecionadosInfo = clientesDisponiveis.filter((item) => cdClientesSelecionados.includes(item.cdCliente));

    const alternarTodosModulosParaTodosClientes = () => {
        const todosModulosSelecionados = clientesSelecionadosInfo.length > 0 &&
            clientesSelecionadosInfo.every((cliente) =>
                modulos.length > 0 &&
                (modulosPorCliente[cliente.cdCliente] || []).length === modulos.length
            );

        setModulosPorCliente((atual) => {
            const copia = { ...atual };
            clientesSelecionadosInfo.forEach((cliente) => {
                copia[cliente.cdCliente] = todosModulosSelecionados ? [] : modulos.map((modulo) => modulo.cdModulo);
            });
            return copia;
        });
    };

    const alternarCliente = (cdClienteAlvo) => {
        setCdClientesSelecionados((atual) => {
            const jaSelecionado = atual.includes(cdClienteAlvo);
            const novoSelecionados = jaSelecionado ? atual.filter((id) => id !== cdClienteAlvo) : [...atual, cdClienteAlvo];

            setModulosPorCliente((atualModulos) => {
                const copia = { ...atualModulos };
                if (jaSelecionado) {
                    delete copia[cdClienteAlvo];
                } else if (!copia[cdClienteAlvo]) {
                    copia[cdClienteAlvo] = [];
                }
                return copia;
            });

            return novoSelecionados;
        });
    };

    const alternarSelecionarTodosClientes = () => {
        if (cdClientesSelecionados.length === clientesDisponiveis.length) {
            setCdClientesSelecionados([]);
            setModulosPorCliente({});
        } else {
            setCdClientesSelecionados(clientesDisponiveis.map((item) => item.cdCliente));
            setModulosPorCliente((atual) => {
                const copia = { ...atual };
                clientesDisponiveis.forEach((item) => {
                    if (!copia[item.cdCliente]) {
                        copia[item.cdCliente] = [];
                    }
                });
                return copia;
            });
        }
    };

    const alternarModuloDoCliente = (cdClienteAlvo, cdModulo) => {
        setModulosPorCliente((atual) => {
            const modulosDoCliente = atual[cdClienteAlvo] || [];
            const novosModulos = modulosDoCliente.includes(cdModulo)
                ? modulosDoCliente.filter((id) => id !== cdModulo)
                : [...modulosDoCliente, cdModulo];
            return { ...atual, [cdClienteAlvo]: novosModulos };
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!nmUsuario.trim()) {
            setErro('Preencha o nome do usuário.');
            return;
        }
        if (!dsEmail.trim()) {
            // O e-mail é o canal de recuperação de senha do usuário secundário.
            setErro('O e-mail é obrigatório.');
            return;
        }
        if (cdClientesSelecionados.length === 0) {
            setErro('Selecione ao menos um CNPJ para o usuário secundário.');
            return;
        }
        const clienteSemModulo = cdClientesSelecionados.find((id) => (modulosPorCliente[id] || []).length === 0);
        if (clienteSemModulo) {
            setErro('Selecione ao menos um módulo para cada CNPJ vinculado.');
            return;
        }

        setErro('');
        setEnviando(true);
        try {
            const atribuicoes = cdClientesSelecionados.map((id) => ({
                cdCliente: id,
                cdModulos: modulosPorCliente[id] || [],
            }));

            await UsuarioSecundarioService.atualizarDados(cdUsuario, {
                cdUsuarioMaster: portalSession.usuario.cdUsuario,
                nmUsuario: nmUsuario.trim(),
                dsEmail: dsEmail.trim(),
                novaSenha: novaSenha || undefined,
            });

            await UsuarioSecundarioService.atualizarAtribuicoes(cdUsuario, {
                cdUsuarioMaster: portalSession.usuario.cdUsuario,
                atribuicoes,
            });

            if (onUsuarioAtualizado) {
                // Recarrega a lista completa e atualizada na sessão antes de navegar — assim
                // GestaoUsuarios já abre refletindo a edição, sem depender da "foto" do login.
                await onUsuarioAtualizado();
            }

            setSucesso('Usuário atualizado com sucesso.');
            setTimeout(() => navigate('/gestao-usuarios'), 1200);
        } catch (error) {
            setErro(error.message || 'Erro ao atualizar usuário secundário.');
        } finally {
            setEnviando(false);
        }
    };

    if (!souMaster) {
        return (
            <div className="gestao-usuarios-page">
                <p className="gestao-usuarios-restrito">Esta área é exclusiva para o usuário master.</p>
            </div>
        );
    }

    if (carregando) {
        return (
            <div className="dashboard">
                <div className="dashboard-content">
                    <div className="dashboard-painel gestao-usuarios">
                        <p>Carregando usuário...</p>
                    </div>
                </div>
            </div>
        );
    }

    const clientesFiltrados = clientesDisponiveis.filter((cliente) => {
        const busca = filtroCliente.trim().toLowerCase();
        if (!busca) {
            return true;
        }
        return (
            String(cliente.nmCliente || '').toLowerCase().includes(busca) ||
            String(cliente.dsCnpjCpf || '').toLowerCase().includes(busca)
        );
    });

    const modulosFiltrados = modulos.filter((modulo) => {
        const busca = filtroModulo.trim().toLowerCase();
        if (!busca) {
            return true;
        }
        return String(modulo.nmModulo || '').toLowerCase().includes(busca);
    });

    return (
        <div className="dashboard">
            <div className="dashboard-content">
                <div className="dashboard-painel gestao-usuarios">
                    <h3 className='titulo'>Editar usuário secundário</h3>

                    <form className="criar-usuario-form" onSubmit={handleSubmit}>
                        <section className="criar-usuario-secao">
                            <h2>1. Credenciais de acesso</h2>

                            <div className="criar-usuario-grid">
                                <div className="criar-usuario-campo">
                                    <label htmlFor="esec-login">Login</label>
                                    <input
                                        id="esec-login"
                                        type="text"
                                        value={dsLogin}
                                        readOnly
                                        disabled
                                        className="criar-usuario-login-gerado"
                                    />
                                </div>
                                <div className="criar-usuario-campo">
                                    <label htmlFor="esec-nome">Nome do usuário</label>
                                    <input
                                        id="esec-nome"
                                        type="text"
                                        value={nmUsuario}
                                        onChange={(e) => setNmUsuario(e.target.value)}
                                        disabled={enviando}
                                    />
                                </div>

                                <div className="criar-usuario-campo">
                                    <label htmlFor="esec-senha">Nova senha</label>
                                    <input
                                        id="esec-senha"
                                        type="password"
                                        value={novaSenha}
                                        onChange={(e) => setNovaSenha(e.target.value)}
                                        disabled={enviando}
                                    />
                                </div>

                                <div className="criar-usuario-campo">
                                    <label htmlFor="esec-email">E-mail</label>
                                    <input
                                        id="esec-email"
                                        type="email"
                                        value={dsEmail}
                                        onChange={(e) => setDsEmail(e.target.value)}
                                        disabled={enviando}
                                        required
                                    />
                                </div>
                            </div>
                        </section>

                        <section className="criar-usuario-secao">
                            <h2>2. Vincular unidades (CNPJs)</h2>
                            <div className="criar-usuario-filtro">
                                <input
                                    type="text"
                                    placeholder="Buscar cliente ou CNPJ..."
                                    value={filtroCliente}
                                    onChange={(e) => setFiltroCliente(e.target.value)}
                                    disabled={enviando}
                                />
                            </div>
                            <div className="criar-usuario-select-all">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={
                                            clientesDisponiveis.length > 0 &&
                                            cdClientesSelecionados.length === clientesDisponiveis.length
                                        }
                                        onChange={alternarSelecionarTodosClientes}
                                        disabled={enviando}
                                    />
                                    Selecionar todos os CNPJs
                                </label>

                                <span>
                                    {cdClientesSelecionados.length} de {clientesDisponiveis.length} selecionados
                                </span>
                            </div>

                            <div className="criar-usuario-clientes-container">
                                <div className="criar-usuario-grid">
                                    {clientesFiltrados.map((item) => (
                                        <label
                                            key={item.cdCliente}
                                            className={`criar-usuario-item ${cdClientesSelecionados.includes(item.cdCliente) ? 'is-checked' : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={cdClientesSelecionados.includes(item.cdCliente)}
                                                onChange={() => alternarCliente(item.cdCliente)}
                                                disabled={enviando}
                                            />
                                            <span>
                                                <strong>{item.nmCliente}</strong>
                                                <small>{item.dsCnpjCpf}</small>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </section>

                        <section className="criar-usuario-secao">
                            <h2>3. Módulos / permissões por CNPJ</h2>
                            <p className="criar-usuario-hint">Cada unidade pode ter um conjunto diferente de módulos liberados.</p>
                            <div className="criar-usuario-filtro">
                                <input
                                    type="text"
                                    placeholder="Buscar módulo..."
                                    value={filtroModulo}
                                    onChange={(e) => setFiltroModulo(e.target.value)}
                                    disabled={enviando}
                                />
                            </div>
                            {clientesSelecionadosInfo.length === 0 ? (
                                <p className="criar-usuario-hint">Selecione ao menos um CNPJ na seção anterior.</p>
                            ) : (
                                <>
                                    <div className="criar-usuario-select-all">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={
                                                    clientesSelecionadosInfo.length > 0 &&
                                                    modulos.length > 0 &&
                                                    clientesSelecionadosInfo.every(
                                                        (cliente) =>
                                                            (modulosPorCliente[cliente.cdCliente] || []).length === modulos.length
                                                    )
                                                }
                                                onChange={alternarTodosModulosParaTodosClientes}
                                                disabled={enviando || modulos.length === 0}
                                            />
                                            Selecionar todos os módulos para todos os CNPJs
                                        </label>
                                        <span>{modulos.length} módulos disponíveis</span>
                                    </div>

                                    <div className="criar-usuario-permissoes-container">
                                        {clientesSelecionadosInfo.map((item) => (
                                            <div key={item.cdCliente} className="criar-usuario-modulos-cliente">
                                                <h3>{item.nmCliente} <small>{item.dsCnpjCpf}</small></h3>
                                                <div className="criar-usuario-grid">
                                                    {modulosFiltrados.map((modulo) => {
                                                        const selecionado = (modulosPorCliente[item.cdCliente] || []).includes(modulo.cdModulo);
                                                        return (
                                                            <label
                                                                key={modulo.cdModulo}
                                                                className={`criar-usuario-item ${selecionado ? 'is-checked' : ''}`}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selecionado}
                                                                    onChange={() => alternarModuloDoCliente(item.cdCliente, modulo.cdModulo)}
                                                                    disabled={enviando}
                                                                />
                                                                <span><strong>{modulo.nmModulo}</strong></span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </section>

                        {erro && <div className="gestao-usuarios-erro">{erro}</div>}
                        {sucesso && <div className="gestao-usuarios-sucesso">{sucesso}</div>}

                        <div className="criar-usuario-acoes">
                            <button type="submit" className="gestao-usuarios-btn-novo" disabled={enviando}>
                                {enviando ? 'Salvando...' : 'Salvar alterações'}
                            </button>
                            <button type="button" className="gestao-usuarios-btn-toggle" onClick={() => navigate('/gestao-usuarios')} disabled={enviando}>
                                Cancelar
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default EditarUsuarioSecundario;
